import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import ReusableModal from '../Modal/ReusableModal';
import revisionService from '../../utils/apiRequests/revisionService';
import lexicalToPlainText from '../../utils/lexicalPlainText';
import useThemeStyles from '../../hooks/useThemeStyles';
import { useProjectAccess } from '../../contexts/ProjectAccessContext';

const formatWhen = (dateString) => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Google-Docs-style version history for a scene: revision list on the right,
 * plain-text preview of the selected version on the left, one-click restore.
 * Revisions are stored server-side as deltas and reconstructed on demand.
 */
const VersionHistoryModal = ({ open, onClose, scene, onRestored }) => {
  const { colors } = useThemeStyles();
  const { canEdit } = useProjectAccess();

  const sceneKey = scene?._id || scene?.id;

  const [loading, setLoading] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [selectedSeq, setSelectedSeq] = useState(null);
  const [preview, setPreview] = useState({ loading: false, text: '', state: null });
  const [error, setError] = useState(null);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open || !sceneKey) return;
    setError(null);
    setRevisions([]);
    setSelectedSeq(null);
    setPreview({ loading: false, text: '', state: null });
    setLoading(true);
    revisionService.listRevisions(sceneKey).then((response) => {
      setLoading(false);
      if (response.error) {
        setError('Could not load the version history.');
        return;
      }
      const list = response.data?.revisions || [];
      setRevisions(list);
      if (list.length > 0) setSelectedSeq(list[0].seq);
    });
  }, [open, sceneKey]);

  // Load the preview whenever the selection changes.
  useEffect(() => {
    if (!open || !sceneKey || selectedSeq == null) return undefined;
    let cancelled = false;
    setPreview({ loading: true, text: '', state: null });
    revisionService.getRevisionState(sceneKey, selectedSeq).then((response) => {
      if (cancelled) return;
      if (response.error) {
        setPreview({ loading: false, text: '', state: null });
        setError('Could not load that version.');
        return;
      }
      const state = response.data?.state || null;
      setPreview({ loading: false, text: lexicalToPlainText(state), state });
    });
    return () => {
      cancelled = true;
    };
  }, [open, sceneKey, selectedSeq]);

  const handleRestore = useCallback(async () => {
    if (selectedSeq == null) return;
    setRestoring(true);
    setError(null);
    const response = await revisionService.restoreRevision(sceneKey, selectedSeq);
    setRestoring(false);
    if (response.error) {
      setError('Could not restore that version.');
      return;
    }
    onClose();
    if (onRestored) onRestored(response.data?.scene, selectedSeq);
  }, [sceneKey, selectedSeq, onClose, onRestored]);

  const latestSeq = revisions.length > 0 ? revisions[0].seq : null;
  const selectedRevision = revisions.find((revision) => revision.seq === selectedSeq);

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title={`History — ${scene?.name?.trim() ? scene.name : 'Untitled scene'}`}
    >
      <Box sx={{ display: 'flex', gap: 2, width: 'min(760px, 86vw)', minHeight: 360 }}>
        {/* Preview */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            border: `1px solid ${colors.border.subtle}`,
            borderRadius: '8px',
            p: 2,
            overflowY: 'auto',
            maxHeight: '55vh',
            backgroundColor: colors.background.secondary,
          }}
        >
          {preview.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!preview.loading && selectedSeq != null && (
            <Typography
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontSize: '0.875rem',
                lineHeight: 1.7,
                color: colors.text.primary,
                m: 0,
              }}
            >
              {preview.text || '(empty scene)'}
            </Typography>
          )}
          {!preview.loading && selectedSeq == null && !loading && (
            <Typography sx={{ color: colors.text.muted, fontSize: '0.875rem' }}>
              No versions yet — history starts with the next save.
            </Typography>
          )}
        </Box>

        {/* Revision list */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Box sx={{ overflowY: 'auto', maxHeight: '48vh', pr: 0.5 }} role="listbox">
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!loading &&
              revisions.map((revision) => {
                const isSelected = revision.seq === selectedSeq;
                return (
                  <Box
                    key={revision.seq}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    onClick={() => setSelectedSeq(revision.seq)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedSeq(revision.seq);
                      }
                    }}
                    sx={{
                      p: 1.25,
                      mb: 0.5,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      border: `1px solid ${
                        isSelected ? colors.interactive.primary : colors.border.subtle
                      }`,
                      backgroundColor: isSelected ? colors.sidebar.headerHover : 'transparent',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <HistoryIcon sx={{ fontSize: '0.9rem', color: colors.text.secondary }} />
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                        {formatWhen(revision.updatedAt || revision.createdAt)}
                      </Typography>
                      {revision.seq === latestSeq && (
                        <Typography sx={{ fontSize: '0.6875rem', color: colors.text.muted }}>
                          current
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                      {revision.authorName}
                      {typeof revision.wordCount === 'number'
                        ? ` • ${revision.wordCount.toLocaleString()} words`
                        : ''}
                    </Typography>
                    {revision.note && (
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          color: colors.text.muted,
                          fontStyle: 'italic',
                        }}
                      >
                        {revision.note}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            {!loading && revisions.length === 0 && (
              <Typography sx={{ fontSize: '0.8125rem', color: colors.text.muted, p: 1 }}>
                No versions yet. A version is kept every time this scene is saved.
              </Typography>
            )}
          </Box>

          {canEdit && selectedRevision && selectedSeq !== latestSeq && (
            <Button
              variant="contained"
              startIcon={<RestoreIcon />}
              disabled={restoring || preview.loading}
              onClick={handleRestore}
            >
              {restoring ? 'Restoring…' : 'Restore this version'}
            </Button>
          )}
          {canEdit && revisions.length > 0 && (
            <Typography sx={{ fontSize: '0.6875rem', color: colors.text.muted }}>
              Restoring keeps the current text as its own version, so nothing is lost.
            </Typography>
          )}
        </Box>
      </Box>
    </ReusableModal>
  );
};

VersionHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  scene: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onRestored: PropTypes.func,
};

VersionHistoryModal.defaultProps = {
  scene: null,
  onRestored: undefined,
};

export default VersionHistoryModal;
